import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";
import { track } from "@/lib/analytics";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dak3kvvh3l9b18n2bqag";

const businessTypes = [
  "Restaurante",
  "Hamburgueria",
  "Pizzaria",
  "Bar",
  "Cafeteria",
  "Beach Club",
  "Rede",
  "Franquia",
  "Outro",
];

const states = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO",
];

const unitOptions = [
  "1 unidade",
  "2 a 5 unidades",
  "6 a 20 unidades",
  "Mais de 20 unidades",
];

export default function LeadForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot anti-spam
    const guard = formData.get("website_alt");
    if (guard && String(guard).trim() !== "") {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams(
      formData as unknown as Record<string, string>
    ).toString();

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (responseText.length < 200 ? responseText : "");
      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.includes("spam") || serverMsg.includes("form data is spam"));

      if (res.ok && parsed?.code === "OK") {
        track("restaurant_lead", { status: "success" });
        setStatus("success");
        form.reset();
      } else {
        track("restaurant_lead", { status: "error" });
        setStatus("error");
        setError(
          isSpam || !serverMsg
            ? "Não foi possível enviar. Tente novamente."
            : serverMsg
        );
      }
    } catch {
      setStatus("error");
      setError("Não foi possível enviar. Verifique sua conexão e tente novamente.");
    }
  }

  return (
    <section id="contato" className="bg-background-100 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="Demonstração"
          title="Quero conhecer a PlayMenu."
          subtitle="Preencha os dados e um especialista entrará em contato para mostrar como transformar seu cardápio."
        />

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:items-start">
          {/* Benefícios laterais */}
          <div className="flex flex-col gap-5 lg:sticky lg:top-28">
            {[
              {
                icon: "ri-video-chat-line",
                title: "Demonstração personalizada",
                text: "Veja a PlayMenu rodando com o seu tipo de cardápio.",
              },
              {
                icon: "ri-flashlight-line",
                title: "Resposta rápida",
                text: "Falamos no seu WhatsApp, sem burocracia.",
              },
              {
                icon: "ri-shield-check-line",
                title: "Sem compromisso",
                text: "Conheça a plataforma antes de decidir.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="reveal flex items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Formulário */}
          <div className="reveal rounded-2xl border border-background-200 bg-background-50 p-7 md:p-9">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-check-line text-3xl" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
                  Recebemos seu pedido!
                </h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-foreground-600">
                  Em breve um especialista da PlayMenu vai entrar em contato para
                  agendar sua demonstração. Fique de olho no WhatsApp.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-8 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  Enviar outro pedido
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-5"
                noValidate={false}
              >
                {/* Honeypot anti-spam */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-guard"
                />

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm font-semibold text-foreground-800">
                      Nome
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Seu nome"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="restaurant" className="text-sm font-semibold text-foreground-800">
                      Restaurante / Empresa
                    </label>
                    <input
                      id="restaurant"
                      name="restaurant"
                      type="text"
                      required
                      placeholder="Nome do seu negócio"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="whatsapp" className="text-sm font-semibold text-foreground-800">
                      WhatsApp
                    </label>
                    <input
                      id="whatsapp"
                      name="whatsapp"
                      type="tel"
                      required
                      placeholder="(00) 00000-0000"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="businessType" className="text-sm font-semibold text-foreground-800">
                      Tipo de negócio
                    </label>
                    <select
                      id="businessType"
                      name="business_type"
                      required
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    >
                      <option value="">Selecione</option>
                      {businessTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="city" className="text-sm font-semibold text-foreground-800">
                      Cidade
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      required
                      placeholder="Sua cidade"
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="state" className="text-sm font-semibold text-foreground-800">
                      Estado
                    </label>
                    <select
                      id="state"
                      name="state"
                      required
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    >
                      <option value="">Selecione</option>
                      {states.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label htmlFor="units" className="text-sm font-semibold text-foreground-800">
                      Quantidade de unidades
                    </label>
                    <select
                      id="units"
                      name="units"
                      required
                      className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-400/30"
                    >
                      <option value="">Selecione</option>
                      {unitOptions.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {status === "error" && (
                  <p className="rounded-md border border-primary-300 bg-primary-100/60 px-4 py-3 text-sm text-foreground-800">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="mt-2 inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-foreground-950 transition-all hover:bg-primary-400 hover:scale-[1.01]"
                >
                  Quero uma demonstração
                  <i className="ri-arrow-right-line ml-2 text-lg" aria-hidden="true" />
                </button>

                <p className="text-center text-xs text-foreground-500">
                  Seus dados estão seguros e não serão compartilhados.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}